import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Edit, Eye, Filter, Plus, Search, Trash2, Upload, X } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { Tooltip } from '../../components/ui/Tooltip';
import { ColumnCustomizer } from '../../components/ColumnCustomizer';
import { PermissionGate } from '../../components/PermissionGate';
import { CourseImportModal } from '../../components/training/CourseImportModal';
import { CourseDeleteModal } from '../../components/training/CourseDeleteModal';
import { useFilters } from '../../hooks/useFilters';
import { useSearch } from '../../hooks/useSearch';
import { usePagination } from '../../hooks/usePagination';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { trainingCoursesStore } from '../../services/trainingCoursesStore';
import { trainingProducts } from '../../data/trainingProducts';
import { courseCharacteristics, courseCommissions, courseTabs, courseVenues } from '../../data/trainingCourseOptions';
import { commissionLabel, getTrainedCourses, getTrainingProduct, parseCourseStatus, venueLabel } from '../../utils/trainingCourses';
import { CourseStatus, TrainingCourse, TrainingCourseDraft } from '../../types/trainingCourse';
const columnsConfig = [{
  key: 'courseCode',
  label: 'Codice corso'
}, {
  key: 'name',
  label: 'Nome corso'
}, {
  key: 'productCode',
  label: 'Tipologia · Codice prodotto'
}, {
  key: 'characteristic',
  label: 'Caratteristica'
}, {
  key: 'venue',
  label: 'Sede'
}, {
  key: 'commission',
  label: 'Commessa'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const onDemandHiddenColumns = ['venue', 'commission'];
const characteristicVariant = {
  Unico: 'neutral',
  Trainante: 'primary',
  Trainato: 'info'
} as const;
export function CoursesPage() {
  const navigate = useNavigate();
  const courses = useTrainingCourses();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseCourseStatus(searchParams.get('tab'));
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [deletingCourse, setDeletingCourse] = useState<TrainingCourse | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const tabCourses = useMemo(() => courses.filter(c => c.status === activeTab), [courses, activeTab]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData,
    activeFilterCount
  } = useFilters(tabCourses);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filteredData, ['name', 'courseCode', 'productCode', 'clientName']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, activeTab]);
  const openCreate = () => navigate(`/training/corsi/nuovo?stato=${activeTab}`);
  const openView = (course: TrainingCourse) => navigate(`/training/corsi/${course.id}`);
  const openEdit = (course: TrainingCourse) => navigate(`/training/corsi/${course.id}/modifica`);
  const handleImport = (drafts: TrainingCourseDraft[]) => trainingCoursesStore.importMany(drafts);
  const handleDelete = (course: TrainingCourse) => trainingCoursesStore.remove(course.id);
  const handleExport = () => {
    const header = ['Codice corso', 'Nome corso', 'Tipologia', 'Prodotto', 'Codice prodotto', 'Acquisto prodotto esterno', 'Caratteristica', ...(isOnDemand ? [] : ['Sede', 'Commessa'])];
    const rows = searchResult.map(c => [c.courseCode, c.name, getTrainingProduct(c.productCode)?.type || '', getTrainingProduct(c.productCode)?.name || '', c.productCode, c.externalProduct ? 'Sì' : 'No', c.characteristic, ...(isOnDemand ? [] : [venueLabel(c.venue), commissionLabel(c)])]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `corsi-${activeTab}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const isOnDemand = activeTab === 'ondemand';
  // Sede and Commessa do not apply to on-demand courses
  const tabColumns = isOnDemand ? columnsConfig.filter(c => !onDemandHiddenColumns.includes(c.key)) : columnsConfig;
  const show = (key: string) => visibleColumns.includes(key) && tabColumns.some(c => c.key === key);
  const shownColumns = tabColumns.filter(c => visibleColumns.includes(c.key));
  const handleTabChange = (tab: CourseStatus) => {
    if (tab === 'ondemand') {
      setFilter('venue', '');
      setFilter('commission', '');
    }
    setSearchParams({
      tab
    }, {
      replace: true
    });
  };
  return <Layout data-id="element-6011" data-mp="1ulq7ofcbql4k">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-6012" data-mp="1yp4mj2oj0vgf">
        <div data-id="element-6013" data-mp="23g4myhwutvno">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6014" data-mp="23fl2atftkt5y">Corsi</h1>
          <p className="text-slate-500 mt-1" data-id="element-6015" data-mp="28exza70engm9">
            Creazione e gestione dei corsi di formazione.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-6016" data-mp="2detvhk2v23r9">
          <Button variant="flat" color="secondary" startContent={<Upload className="h-4 w-4" data-id="element-6018" data-mp="2idn5v3mupen7" />} onClick={() => setIsImportOpen(true)} data-id="element-6017" data-mp="2dfvfie1uu0l4">
            Import massivo
          </Button>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-6020" data-mp="22et1rzf2nf0h" />} onClick={handleExport} data-id="element-6019" data-mp="1nq57dwgypbc4">
            Esporta
          </Button>
          <PermissionGate module="training" action="create" data-id="element-6021" data-mp="1xnt58v9v6yyi">
            <Button onClick={openCreate} startContent={<Plus className="h-4 w-4" data-id="element-6023" data-mp="2bdms581t795l" />} data-id="element-6022" data-mp="2bfhe8poth7b9">
              Nuovo corso
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="bg-slate-100 p-1 rounded-xl flex items-center w-fit max-w-full overflow-x-auto mb-6" role="tablist" aria-label="Stato dei corsi" data-id="element-6024" data-mp="2le6u6cx5khov">
        {courseTabs.map(tab => {
        const count = courses.filter(c => c.status === tab.id).length;
        const isActive = activeTab === tab.id;
        return <button key={tab.id} type="button" role="tab" aria-selected={isActive} onClick={() => handleTabChange(tab.id)} className={`px-4 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors duration-150 ${isActive ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6025" data-mp="m2w0fwvg2qtk">
              {tab.label}
              <span className={`text-xs tabular-nums ${isActive ? 'text-seform-orange' : 'text-slate-400'}`} data-id="element-6026" data-mp="2evu0qg0r2nqk">
                {count}
              </span>
            </button>;
      })}
      </div>

      <Card className="mb-6" noPadding data-id="element-6027" data-mp="29yamhg41yaym">
        <div className="p-4 flex flex-col gap-4" data-id="element-6028" data-mp="29zd9ve261v9s">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6029" data-mp="2a056ms7i1z1n">
            <div className="flex-1" data-id="element-6030" data-mp="395ohtppeaws">
              <Input placeholder="Cerca nome corso, codice corso, codice prodotto, azienda..." startContent={<Search className="h-4 w-4" data-id="element-6032" data-mp="2r604i7tx2bzk" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-6031" data-mp="361cwb9wscw3" />
            </div>
            <div className="flex gap-2" data-id="element-6033" data-mp="2icddsw4kfyx8">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6035" data-mp="28gk55ntywpkq" />} data-id="element-6034" data-mp="2ibc34srgpeg1">
                Filtri{' '}
                {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6036" data-mp="1zeaumxyp8w7c">
                    {activeFilterCount}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={tabColumns} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="training-courses" lockedColumns={['name']} data-id="element-6037" data-mp="1uh2e11t79087" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100" data-id="element-6038" data-mp="5671g8spmz3i">
              <Select label="Tipologia di corso" value={filters.productCode || ''} onChange={e => setFilter('productCode', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...trainingProducts.map(p => ({
            value: p.code,
            label: `${p.type} — ${p.name}`
          }))]} data-id="element-6039" data-mp="9wngtyg3cusi" />
              <Select label="Caratteristica" value={filters.characteristic || ''} onChange={e => setFilter('characteristic', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...courseCharacteristics.map(c => ({
            value: c,
            label: c
          }))]} data-id="element-6040" data-mp="3neg0bm29315z" />
              {!isOnDemand && <>
                  <Select label="Sede" value={filters.venue || ''} onChange={e => setFilter('venue', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, ...courseVenues]} data-id="element-6041" data-mp="vtxjothv3xm4" />
                  <Select label="Commessa" value={filters.commission || ''} onChange={e => setFilter('commission', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, ...courseCommissions]} data-id="element-6042" data-mp="7x07z1o87ryx" />
                </>}
              <div className="col-span-full flex justify-end" data-id="element-6043" data-mp="1ta391lzxl3ov">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-6045" data-mp="3iq71igi103v2" />} data-id="element-6044" data-mp="1t9tls669tamv">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6046" data-mp="390dodb28jgop">
        <div className="overflow-x-auto" data-id="element-6047" data-mp="38z2m5fofugzp">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6048" data-mp="3433yjuaxp30c">
            <thead data-id="element-6049" data-mp="2z2zlke98iqev">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6050" data-mp="2z3hmvpar7utn">
                {shownColumns.map(col => <th key={col.key} className={`px-4 py-4 whitespace-nowrap ${col.key === 'actions' ? 'pr-6' : ''}`} data-id="element-6051" data-mp="2q21chhjxvspf">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6052" data-mp="jblgkbyttf4e">
              {paginatedData.length === 0 && <tr data-id="element-6053" data-mp="2ds1yidatzv6q">
                  <td colSpan={shownColumns.length} className="px-4 py-16 text-center text-sm text-slate-500" data-id="element-6054" data-mp="2drjv8wpr4gpb">
                    {tabCourses.length === 0 ? 'Nessun corso in questa sezione.' : 'Nessun corso corrisponde alla ricerca o ai filtri.'}
                  </td>
                </tr>}
              {paginatedData.map(course => {
              const product = getTrainingProduct(course.productCode);
              const trainedCount = getTrainedCourses(courses, course.id).length;
              return <tr key={course.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-6055" data-mp="2mmztrr1gbk18">
                    {show('courseCode') && <td className="px-4 py-4 text-sm font-mono text-slate-600 whitespace-nowrap" data-id="element-6056" data-mp="1b7tcemwr2bl0">
                        {course.courseCode}
                      </td>}
                    {show('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-6057" data-mp="3pdralw6g942d">
                        {course.name || '-'}
                        {course.externalProduct && <span className="block text-xs font-normal text-slate-500 mt-0.5" data-id="element-6058" data-mp="36aub7iyw1xs">
                            Acquisto prodotto esterno
                          </span>}
                      </td>}
                    {show('productCode') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6059" data-mp="3kdwijpaxu8fq">
                        {product?.type || '-'}
                        <span className="block text-xs text-slate-400 mt-0.5" data-id="element-6060" data-mp="3fn6c08qrzzjg">
                          {product?.name && `${product.name} · `}
                          <span className="font-mono" data-id="element-6061" data-mp="2rvr6k73pd23z">{course.productCode}</span>
                        </span>
                      </td>}
                    {show('characteristic') && <td className="px-4 py-4" data-id="element-6062" data-mp="30jn1jwrnpas9">
                        <Badge variant={characteristicVariant[course.characteristic]} size="sm" data-id="element-6063" data-mp="35lm639z4udqs">
                          {course.characteristic}
                          {course.characteristic === 'Trainante' && ` (${trainedCount})`}
                        </Badge>
                      </td>}
                    {show('venue') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6064" data-mp="3ta5dlnacymht">
                        {venueLabel(course.venue)}
                      </td>}
                    {show('commission') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6065" data-mp="1ywnm5p6u15hi">
                        {commissionLabel(course) || '-'}
                      </td>}
                    {show('actions') && <td className="px-4 py-4 pr-6" data-id="element-6066" data-mp="1wcr9r9iwvu8r">
                        <div className="flex items-center gap-1" data-id="element-6067" data-mp="1rkziewrxlkhk">
                          <Tooltip content="Visualizza corso" data-id="element-6068" data-mp="1rjoft0a82is1">
                            <Button variant="light" size="sm" className="!px-1" aria-label="Visualizza corso" onClick={() => openView(course)} data-id="element-6069" data-mp="1ml3gd32so34n">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-6070" data-mp="2ac0ftgdel7z0" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="training" action="edit" data-id="element-6071" data-mp="20db6wd3hixuw">
                            <Tooltip content="Modifica corso" data-id="element-6072" data-mp="20fdwiw0q6oz3">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Modifica corso" onClick={() => openEdit(course)} data-id="element-6073" data-mp="tya7tyj5xzk3">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-6074" data-mp="3h3kev499478k" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="training" action="delete" data-id="element-6075" data-mp="38732xyt2c5br">
                            <Tooltip content="Elimina corso" data-id="element-6076" data-mp="2tfapjsctx1co">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Elimina corso" onClick={() => setDeletingCourse(course)} data-id="element-6077" data-mp="2y7c7nuwohmam">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-6078" data-mp="2ahrk0ypky0v0" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6079" data-mp="25tqj2c7pla1x">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6080" data-mp="25qch352yj2v0" />
        </div>
      </Card>

      <CourseImportModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} status={activeTab} onImport={handleImport} data-id="element-6081" data-mp="2arjq17gu6839" />
      <CourseDeleteModal course={deletingCourse} trainedCount={deletingCourse ? getTrainedCourses(courses, deletingCourse.id).length : 0} onClose={() => setDeletingCourse(null)} onConfirm={handleDelete} data-id="element-6082" data-mp="2jl538mdcrhak" />
    </Layout>;
}